import { useState, useRef, useEffect } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { useUnreadCount } from '../hooks/useNotifications';
import NotificationDropdown from './NotificationDropdown';

type NotificationBellVariant = 'popover' | 'panel' | 'dock';
type NotificationBellTheme = 'default' | 'edgemail';

interface NotificationBellProps {
  variant?: NotificationBellVariant;
  theme?: NotificationBellTheme;
  open?: boolean;
  onToggle?: () => void;
  onClose?: () => void;
  renderDropdown?: boolean;
}

export default function NotificationBell({
  variant = 'popover',
  theme = 'default',
  open,
  onToggle,
  onClose,
  renderDropdown = true,
}: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data } = useUnreadCount();
  const count = data?.count ?? 0;
  const isControlled = typeof open === 'boolean';
  const resolvedOpen = isControlled ? open : isOpen;

  // Close on click outside
  useEffect(() => {
    if (!resolvedOpen || variant === 'dock' || !renderDropdown) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (onClose) {
          onClose();
        } else {
          setIsOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [resolvedOpen, onClose]);

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setIsOpen((p) => !p);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={handleToggle}
        className={
          theme === 'edgemail'
            ? `p-1.5 rounded-full text-[var(--em-text-tertiary)] hover:bg-[var(--em-surface-hover)] hover:text-[var(--em-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--em-state-focus)] focus:ring-offset-2 focus:ring-offset-[var(--em-surface-ribbon)] transition-colors relative ${
                resolvedOpen ? 'bg-[var(--em-state-selected)] text-[var(--em-accent-primary)]' : ''
              }`
            : `p-2 rounded-full text-[var(--lex-text-tertiary)] hover:bg-[var(--lex-surface-hover)] hover:text-[var(--lex-text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--lex-focus-ring)] focus:ring-offset-2 focus:ring-offset-[var(--lex-surface-header)] transition-colors relative ${
                resolvedOpen ? 'bg-[var(--lex-accent-primary-soft)] text-[var(--lex-accent-primary)]' : ''
              }`
        }
        title="Notifications"
      >
        <BellIcon className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-[var(--lex-accent-contrast)] bg-[var(--lex-status-danger)] rounded-full leading-none">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {renderDropdown && variant !== 'dock' && (
        <NotificationDropdown
          isOpen={resolvedOpen}
          onClose={() => {
            if (onClose) {
              onClose();
            } else {
              setIsOpen(false);
            }
          }}
          variant={variant}
          theme={theme}
        />
      )}
    </div>
  );
}
